import React from 'react';

export type TransactionStatusSource = 'poolpay' | 'viyona';

interface TransactionStatusModalProps {
  isOpen: boolean;
  onClose: () => void;
  statusData: any;
  /** PoolPay: only PoolPay enquiry API fields + transaction_status. Viyona: only Viyona payout fields. */
  source?: TransactionStatusSource;
}

const TransactionStatusModal: React.FC<TransactionStatusModalProps> = ({
  isOpen,
  onClose,
  statusData,
  source = 'viyona',
}) => {
  if (!isOpen || !statusData || !statusData.data) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const data = statusData.data;

  const parseTransactionDate = (dateString: string): Date | null => {
    if (!dateString) return null;
    const ddMMyyyyPattern = /^(\d{2})-(\d{2})-(\d{4})$/;
    const match = dateString.match(ddMMyyyyPattern);
    if (match) {
      const [, day, month, year] = match;
      const date = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
      if (
        date.getFullYear() === parseInt(year, 10) &&
        date.getMonth() === parseInt(month, 10) - 1 &&
        date.getDate() === parseInt(day, 10)
      ) {
        return date;
      }
    }
    const parsed = new Date(dateString);
    return !isNaN(parsed.getTime()) ? parsed : null;
  };

  const formatDate = (dateString: string | Date | null, includeTime: boolean = true): string => {
    if (!dateString) return 'N/A';
    let date: Date | null = null;
    let isDateOnly = false;

    if (dateString instanceof Date) {
      date = dateString;
    } else if (typeof dateString === 'string') {
      const ddMMyyyyPattern = /^(\d{2})-(\d{2})-(\d{4})$/;
      if (ddMMyyyyPattern.test(dateString)) {
        isDateOnly = true;
      }
      date = parseTransactionDate(dateString);
    }

    if (!date || isNaN(date.getTime())) {
      return 'Invalid Date';
    }

    if (isDateOnly || !includeTime) {
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    }

    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  /** PoolPay Merchant Initiated Status Enquiry — badge driven only by transaction_status from API */
  const poolPayPayoutBadge = (transactionStatus: string | undefined) => {
    const u = (transactionStatus || '').trim().toLowerCase();
    if (u === 'completed')
      return {
        circle: 'bg-emerald-500 text-white',
        text: 'text-emerald-700',
        icon: '✓' as const,
      };
    if (u === 'failed')
      return {
        circle: 'bg-red-500 text-white',
        text: 'text-red-700',
        icon: '✕' as const,
      };
    if (u === 'created' || u === 'processing' || u === 'pending')
      return {
        circle: 'bg-amber-500 text-white',
        text: 'text-amber-800',
        icon: '⏳' as const,
      };
    return {
      circle: 'bg-slate-500 text-white',
      text: 'text-slate-700',
      icon: 'ℹ' as const,
    };
  };

  /** Viyona payout list / status — legacy SUCCESS / FAILED strings */
  const viyonaBadge = (status: string) => {
    const statusUpper = (status || '').toUpperCase();
    switch (statusUpper) {
      case 'SUCCESS':
        return {
          circle: 'bg-emerald-500 text-white',
          text: 'text-emerald-600',
          icon: '✓' as const,
        };
      case 'FAILED':
        return {
          circle: 'bg-red-500 text-white',
          text: 'text-red-600',
          icon: '✕' as const,
        };
      case 'PENDING':
      case 'PROCESSING':
        return {
          circle: 'bg-amber-500 text-white',
          text: 'text-amber-600',
          icon: '⏳' as const,
        };
      default:
        return {
          circle: 'bg-slate-500 text-white',
          text: 'text-slate-600',
          icon: 'ℹ' as const,
        };
    }
  };

  const transactionDate =
    data.transaction_date ||
    data.decrypted_response?.transaction_date ||
    data.decrypted_response?.response_body?.transaction_date;

  // ——— PoolPay only (live status enquiry API body in statusData.data) ———
  if (source === 'poolpay') {
    const payoutStatus = data.transaction_status;
    const badge = poolPayPayoutBadge(typeof payoutStatus === 'string' ? payoutStatus : undefined);

    return (
      <div
        className="fixed inset-0 bg-black/60 flex items-center justify-center z-[10000] p-4 backdrop-blur-sm"
        onClick={handleBackdropClick}
      >
        <div
          className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between p-5 border-b border-slate-200">
            <div className="flex items-center gap-3">
              <div
                className={`w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold ${badge.circle}`}
              >
                {badge.icon}
              </div>
              <div>
                <h2 className="text-lg font-bold text-slate-800 m-0">PoolPay status</h2>
                <span className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mt-0.5">
                  transaction_status (enquiry API)
                </span>
                <span className={`text-base font-bold ${badge.text}`}>
                  {payoutStatus != null && payoutStatus !== '' ? String(payoutStatus) : '—'}
                </span>
                {(data.status != null || data.message != null) && (
                  <span className="block text-xs text-slate-500 mt-1">
                    Enquiry {data.status != null ? `status: ${data.status}` : ''}
                    {data.message != null ? `${data.status != null ? ' · ' : ''}message: ${data.message}` : ''}
                  </span>
                )}
              </div>
            </div>
            <button
              type="button"
              className="w-10 h-10 flex items-center justify-center rounded-lg border-none text-slate-500 hover:bg-slate-100 cursor-pointer"
              onClick={onClose}
              aria-label="Close"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          </div>

          <div className="p-5 overflow-y-auto flex-1">
            <div className="flex flex-col lg:flex-row lg:items-stretch gap-4 min-h-0">
              <div className="min-w-0 flex-1 flex flex-col gap-3">
              {data.order_id && (
                <div className="flex gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100">
                  <span className="text-xl">📋</span>
                  <div className="min-w-0 flex-1">
                    <span className="block text-xs font-semibold text-slate-500 uppercase">order_id</span>
                    <span className="block text-sm font-mono text-slate-800 break-all">{data.order_id}</span>
                  </div>
                </div>
              )}
              {data.transaction_id && (
                <div className="flex gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100">
                  <span className="text-xl">🔢</span>
                  <div className="min-w-0 flex-1">
                    <span className="block text-xs font-semibold text-slate-500 uppercase">transaction_id</span>
                    <span className="block text-sm font-mono text-slate-800 break-all">{data.transaction_id}</span>
                  </div>
                </div>
              )}
              {data.pay_id != null && data.pay_id !== '' && (
                <div className="flex gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100">
                  <span className="text-xl">🏷️</span>
                  <div className="min-w-0 flex-1">
                    <span className="block text-xs font-semibold text-slate-500 uppercase">pay_id</span>
                    <span className="block text-sm font-mono text-slate-800 break-all">{String(data.pay_id)}</span>
                  </div>
                </div>
              )}
              {data.transaction_type && (
                <div className="flex gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100">
                  <span className="text-xl">↔️</span>
                  <div className="min-w-0 flex-1">
                    <span className="block text-xs font-semibold text-slate-500 uppercase">transaction_type</span>
                    <span className="block text-sm text-slate-800">{data.transaction_type}</span>
                  </div>
                </div>
              )}
              {data.purpose && (
                <div className="flex gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100">
                  <span className="text-xl">🎯</span>
                  <div className="min-w-0 flex-1">
                    <span className="block text-xs font-semibold text-slate-500 uppercase">purpose</span>
                    <span className="block text-sm text-slate-800">{data.purpose}</span>
                  </div>
                </div>
              )}
              {(data.beneficiary_name ||
                data.beneficiary_mobile_number ||
                data.beneficiary_account_number ||
                data.beneficiary_ifsc) && (
                <div className="flex gap-3 p-3 rounded-xl bg-indigo-50 border border-indigo-100">
                  <span className="text-xl">👤</span>
                  <div className="min-w-0 flex-1 space-y-1">
                    <span className="block text-xs font-semibold text-slate-500 uppercase">Beneficiary</span>
                    {data.beneficiary_name && (
                      <span className="block text-sm text-slate-800">{data.beneficiary_name}</span>
                    )}
                    {data.beneficiary_mobile_number && (
                      <span className="block text-sm font-mono text-slate-700">{data.beneficiary_mobile_number}</span>
                    )}
                    {data.beneficiary_account_number && (
                      <span className="block text-xs font-mono text-slate-600 break-all">
                        A/C {data.beneficiary_account_number}
                        {data.beneficiary_ifsc ? ` · ${data.beneficiary_ifsc}` : ''}
                      </span>
                    )}
                  </div>
                </div>
              )}
              {data.acquirer_status_message && (
                <div className="flex gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100">
                  <span className="text-xl">🏦</span>
                  <div className="min-w-0 flex-1">
                    <span className="block text-xs font-semibold text-slate-500 uppercase">acquirer_status_message</span>
                    <span className="block text-sm text-slate-800">{data.acquirer_status_message}</span>
                  </div>
                </div>
              )}
              {data.amount != null && data.amount !== '' && (
                <div className="flex gap-3 p-3 rounded-xl bg-violet-50 border border-violet-100">
                  <span className="text-xl">💰</span>
                  <div className="min-w-0 flex-1">
                    <span className="block text-xs font-semibold text-slate-500 uppercase">amount</span>
                    <span className="block text-base font-bold text-slate-800">
                      ₹
                      {new Intl.NumberFormat('en-IN', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }).format(parseFloat(String(data.amount)))}
                    </span>
                  </div>
                </div>
              )}
              {data.remarks != null && data.remarks !== '' && (
                <div className="flex gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100">
                  <span className="text-xl">📝</span>
                  <div className="min-w-0 flex-1">
                    <span className="block text-xs font-semibold text-slate-500 uppercase">remarks</span>
                    <span className="block text-sm text-slate-800">{String(data.remarks)}</span>
                  </div>
                </div>
              )}
              {data.version && (
                <div className="flex gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100">
                  <span className="text-xl">ℹ️</span>
                  <div className="min-w-0 flex-1">
                    <span className="block text-xs font-semibold text-slate-500 uppercase">version</span>
                    <span className="block text-sm font-mono text-slate-700">{data.version}</span>
                  </div>
                </div>
              )}
              </div>

              <aside className="min-w-0 flex-1 lg:max-w-[min(50%,26rem)] flex flex-col rounded-xl border border-slate-700 overflow-hidden bg-slate-900 shrink-0">
                <div className="px-3 py-2.5 bg-slate-800 text-slate-200 text-xs font-semibold border-b border-slate-700">
                  Raw PoolPay enquiry response (JSON)
                </div>
                <pre className="p-4 text-slate-100 text-xs overflow-auto m-0 font-mono flex-1 min-h-[12rem] max-h-[min(58vh,26rem)] whitespace-pre-wrap break-words">
                  {JSON.stringify(data, null, 2)}
                </pre>
              </aside>
            </div>
          </div>

          <div className="p-5 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="w-full py-3 px-5 rounded-xl bg-violet-600 text-white font-semibold text-sm flex items-center justify-center gap-2 cursor-pointer hover:bg-violet-700"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Got it!
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ——— Viyona only ———
  const vStatus = data.status || 'UNKNOWN';
  const vb = viyonaBadge(vStatus);

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-[10000] p-4 backdrop-blur-sm"
      onClick={handleBackdropClick}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div
              className={`w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold ${vb.circle}`}
            >
              {vb.icon}
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-800 m-0">Transaction Status</h2>
              <span className={`text-sm font-semibold ${vb.text}`}>{vStatus}</span>
            </div>
          </div>
          <button
            type="button"
            className="w-10 h-10 flex items-center justify-center rounded-lg border-none text-slate-500 hover:bg-slate-100 cursor-pointer"
            onClick={onClose}
            aria-label="Close"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div className="p-5 overflow-y-auto flex-1">
          <div className="grid grid-cols-1 gap-3">
            {data.merchant_reference_number && (
              <div className="flex gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100">
                <span className="text-xl">📋</span>
                <div className="min-w-0 flex-1">
                  <span className="block text-xs font-semibold text-slate-500 uppercase">Merchant Reference</span>
                  <span className="block text-sm font-mono text-slate-800 truncate">{data.merchant_reference_number}</span>
                </div>
              </div>
            )}
            {data.viyona_transaction_id && (
              <div className="flex gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100">
                <span className="text-xl">🔢</span>
                <div className="min-w-0 flex-1">
                  <span className="block text-xs font-semibold text-slate-500 uppercase">Transaction ID</span>
                  <span className="block text-sm font-mono text-slate-800 truncate">{data.viyona_transaction_id}</span>
                </div>
              </div>
            )}
            {data.amount && (
              <div className="flex gap-3 p-3 rounded-xl bg-violet-50 border border-violet-100">
                <span className="text-xl">💰</span>
                <div className="min-w-0 flex-1">
                  <span className="block text-xs font-semibold text-slate-500 uppercase">Amount</span>
                  <span className="block text-base font-bold text-slate-800">
                    ₹
                    {new Intl.NumberFormat('en-IN', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }).format(parseFloat(data.amount))}
                  </span>
                </div>
              </div>
            )}
            {data.bank_reference_number && (
              <div className="flex gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100">
                <span className="text-xl">🏦</span>
                <div className="min-w-0 flex-1">
                  <span className="block text-xs font-semibold text-slate-500 uppercase">Bank Reference</span>
                  <span className="block text-sm font-mono text-slate-800 truncate">{data.bank_reference_number}</span>
                </div>
              </div>
            )}
            {transactionDate && (
              <div className="flex gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100">
                <span className="text-xl">📅</span>
                <div className="min-w-0 flex-1">
                  <span className="block text-xs font-semibold text-slate-500 uppercase">Transaction Date</span>
                  <span className="block text-sm text-slate-800">{formatDate(transactionDate, false)}</span>
                </div>
              </div>
            )}
            {data.created_at && (
              <div className="flex gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100">
                <span className="text-xl">🕐</span>
                <div className="min-w-0 flex-1">
                  <span className="block text-xs font-semibold text-slate-500 uppercase">Transaction Time</span>
                  <span className="block text-sm text-slate-800">{formatDate(data.created_at)}</span>
                </div>
              </div>
            )}
            {data.updated_at && (
              <div className="flex gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100">
                <span className="text-xl">🔄</span>
                <div className="min-w-0 flex-1">
                  <span className="block text-xs font-semibold text-slate-500 uppercase">Last Updated</span>
                  <span className="block text-sm text-slate-800">{formatDate(data.updated_at)}</span>
                </div>
              </div>
            )}
          </div>

          {data.error_message && (
            <div className="mt-4 flex gap-3 p-4 rounded-xl border-2 border-amber-200 bg-amber-50">
              <span className="text-xl flex-shrink-0">⚠️</span>
              <p className="text-sm text-amber-800 m-0">{data.error_message}</p>
            </div>
          )}

          {data.decrypted_response && (
            <details className="mt-4 rounded-xl border border-slate-200 overflow-hidden">
              <summary className="p-3 bg-slate-100 cursor-pointer text-sm font-semibold text-slate-700">
                View Decrypted Response Data
              </summary>
              <pre className="p-4 bg-slate-900 text-slate-100 text-xs overflow-x-auto m-0 font-mono">
                {JSON.stringify(data.decrypted_response, null, 2)}
              </pre>
            </details>
          )}
        </div>

        <div className="p-5 border-t border-slate-200">
          <button
            type="button"
            onClick={onClose}
            className="w-full py-3 px-5 rounded-xl bg-violet-600 text-white font-semibold text-sm flex items-center justify-center gap-2 cursor-pointer hover:bg-violet-700"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Got it!
          </button>
        </div>
      </div>
    </div>
  );
};

export default TransactionStatusModal;
